/**
 * SellerSelector - Componente seletor de vendedores
 * 
 * Componente reutilizável para selecionar vendedores em formulários.
 * Usado em: DealFormDialog, CreateContactDialog, EditContactDialog
 * 
 * @version 1.0.0
 */

import React from 'react';
import { Check, ChevronsUpDown, UserCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { useCrmSellers } from '@/hooks/useCrmSellers';
import { Skeleton } from '@/components/ui/skeleton';

// Props do componente
interface SellerSelectorProps {
  value: string | null | undefined;
  onSelect: (sellerId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
}

/**
 * Componente para seleção de vendedor com busca e avatar
 */
export function SellerSelector({
  value,
  onSelect,
  placeholder = 'Selecione um vendedor...',
  disabled = false,
  className,
  allowClear = true,
}: SellerSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const { activeSellers, isLoading } = useCrmSellers();

  // Vendedor selecionado atualmente
  const selectedSeller = activeSellers.find(s => s.id === value);

  // Handler para seleção
  const handleSelect = (sellerId: string) => {
    if (sellerId === value) {
      // Clicou no mesmo - limpa seleção
      onSelect(null);
    } else {
      onSelect(sellerId);
    }
    setOpen(false);
  };

  // Handler para limpar seleção
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  if (isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !selectedSeller && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 truncate">
            {selectedSeller ? (
              <>
                <SmartAvatar
                  src={selectedSeller.avatar_url}
                  name={selectedSeller.name}
                  size="xs"
                  use3DFallback
                />
                <span className="truncate">{selectedSeller.name}</span>
              </>
            ) : (
              <>
                <UserCheck className="h-4 w-4 opacity-50" />
                <span>{placeholder}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            {allowClear && selectedSeller && (
              <X
                className="h-4 w-4 opacity-50 hover:opacity-100"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar vendedor..." />
          <CommandList>
            <CommandEmpty>Nenhum vendedor encontrado.</CommandEmpty>
            <CommandGroup>
              {activeSellers.map(seller => (
                <CommandItem
                  key={seller.id}
                  value={seller.name}
                  onSelect={() => handleSelect(seller.id)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <SmartAvatar
                    src={seller.avatar_url}
                    name={seller.name}
                    size="sm"
                    use3DFallback
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium truncate">{seller.name}</span>
                    {seller.email && (
                      <span className="text-xs text-muted-foreground truncate">
                        {seller.email}
                      </span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      value === seller.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
